import { useEffect } from "react";
import { useContexts } from "@/hooks/useContexts";
import { useSDK } from "@/hooks/useSDK";
import { BackgroundProcessStatus } from "@/context/Global/types";
import { ToastStatusEnum } from "@/utils/Enums";

export const BackgroundProcessMonitor = () => {
  const { sdk } = useSDK();
  const { globalState, globalDispatch, showToast, authState } = useContexts();

  // Clear background processes when user logs out
  useEffect(() => {
    if (!authState.isAuthenticated) {
      globalDispatch({
        type: "SET_BACKGROUND_PROCESSES",
        payload: [],
      });
    }
  }, [authState.isAuthenticated, globalDispatch]);

  useEffect(() => {
    // Don't start polling if user is not authenticated
    if (!authState.isAuthenticated) {
      return;
    }

    const pollingInterval = setInterval(async () => {
      const pendingProcesses =
        globalState.backgroundProcesses?.filter(
          (process) =>
            process.type === "websiteAnalysis" &&
            process.status === BackgroundProcessStatus.PENDING
        ) || [];

      if (pendingProcesses.length === 0) {
        return;
      }

      // Update all pending processes
      for (const process of pendingProcesses) {
        try {
          const { jobId } = process.data;
          console.log(`Polling job status for job ID: ${jobId}`);

          const result = await sdk.callRawAPI(
            `/v1/api/contentgap/job-status?job_id=${jobId}`,
            {},
            "GET"
          );

          console.log("Job status response:", result);

          if (!result.error) {
            const status = result.status || (result.job && result.job.status);
            const error =
              result.error_message || (result.job && result.job.error);

            if (status === "completed") {
              showToast(
                "Website analysis completed! View the report in Reports section.",
                6000,
                ToastStatusEnum.SUCCESS
              );

              globalDispatch({
                type: "SET_BACKGROUND_PROCESSES",
                payload: globalState.backgroundProcesses.map((p) =>
                  p.id === process.id
                    ? {
                        ...p,
                        status: BackgroundProcessStatus.COMPLETED,
                        lastUpdated: Date.now(),
                      }
                    : p
                ),
              });
            } else if (status === "failed") {
              showToast(
                `Analysis failed: ${error || "Unknown error"}`,
                4000,
                ToastStatusEnum.ERROR
              );

              globalDispatch({
                type: "SET_BACKGROUND_PROCESSES",
                payload: globalState.backgroundProcesses.map((p) =>
                  p.id === process.id
                    ? {
                        ...p,
                        status: BackgroundProcessStatus.FAILED,
                        lastUpdated: Date.now(),
                        data: { ...p.data, error },
                      }
                    : p
                ),
              });
            }
          }
        } catch (error) {
          console.error("Error polling job status:", error);
        }
      }
    }, 5000);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [
    globalState.backgroundProcesses,
    globalDispatch,
    sdk,
    showToast,
    authState.isAuthenticated,
  ]);

  return null; // This component doesn't render anything
};
